/* eslint-disable react-refresh/only-export-components --
 * This is the consolidated review-card module: components plus the
 * `getEmailLinks` / `getAttachments` helpers they share. Splitting the
 * helpers into a sibling file would touch every consumer; co-location
 * keeps the public surface of the review module a single import. */
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Image as ImageIcon,
  Mail,
  Sparkles,
  Clock,
  MapPin,
  SquareCheckBig,
  Plus,
} from 'lucide-react';
import type { Ref } from 'react';
import {
  formatDateRange,
  formatShortDate,
  formatTime,
  formatTimeRange,
} from '@/lib/date';
import { resolveAiChildName, type DisambiguationMap } from '@/lib/familyChild';
import { ProfileBadge } from '@/components/ui/ProfileBadge';
import { AvatarGroup } from '@/components/ui/AvatarGroup';
import { TaskEventDateIcon } from '@/components/ui';
import type { Email, ReviewItemWithChild, Child } from '@/types';
import {
  EmailSourcePanel,
  type AttachmentInfo,
} from '@/components/domain/shared/EmailSourcePanel';
import { EventLinkBadge } from '@/components/domain/shared/EventLinkBadge';
import { UploadSourcePanel } from '@/components/domain/shared/UploadSourcePanel';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import { uploadDisplayKind } from '@/lib/uploadKind';
import type { AvailableMember } from './ItemFormModal';
import type { ProposalView } from '@/lib/reviewProposal';
import { ProposalSummary } from './ProposalSummary';
import { REVIEW_COPY } from './reviewCopy';
import { RecurrenceSummary } from './RecurrenceSummary';
import {
  childMentionCaption,
  isMemoryFilled,
  MEMORY_FILL_CAPTION,
} from '@/lib/memoryFill';
import { toTimeKeyTz } from '@/lib/tz';

/** review_item.id → user_ids the user picked in the Members dropdown. */
export type ItemMemberAssignments = Map<string, string[]>;

// --- Types for hierarchical grouping ---

export interface EventGroup {
  event: ReviewItemWithChild;
  relatedTasks: ReviewItemWithChild[];
}

export interface GroupedItems {
  eventGroups: EventGroup[];
  standaloneTasks: ReviewItemWithChild[];
}

/**
 * Proposed-update wiring (YAR-493). Omit it and every card renders exactly
 * as it did before context-aware extraction.
 */
export interface ReviewCardProposals {
  resolve: (item: ReviewItemWithChild) => ProposalView;
  /** Omit to hide "Keep as new" (read-only / committed). */
  onKeepAsNew?: (item: ReviewItemWithChild) => void;
  /** The item whose "Keep as new" write is in flight. */
  keepingAsNewId?: string | null;
}

// =============================================================================
// Event Group Card — event with collapsible related tasks
// =============================================================================

export function EventGroupCard({
  group,
  isExpanded,
  onToggle,
  onEditItem,
  onAddTaskToEvent,
  childrenData,
  disambiguation,
  readOnly = false,
  memberIdsByItem,
  availableMembers,
  timezone,
  proposals,
  onChangeRecurrenceUntil,
}: {
  group: EventGroup;
  isExpanded: boolean;
  onToggle: () => void;
  /** Omit to render the event row in view-only mode (no edit affordance). */
  onEditItem?: (item: ReviewItemWithChild) => void;
  /** Omit to hide the "Add task to event" CTA inside the expanded panel. */
  onAddTaskToEvent?: (event: ReviewItemWithChild) => void;
  childrenData: Child[];
  /** User picks for ambiguous AI mentions, threaded down to badges. */
  disambiguation?: DisambiguationMap;
  readOnly?: boolean;
  /** Member assignments per review_item — drives the right-side AvatarGroup. */
  memberIdsByItem?: ItemMemberAssignments;
  /** Full household roster so member ids can be resolved to avatars. */
  availableMembers?: AvailableMember[];
  /**
   * The profile timezone the item was anchored in. Omit and the card falls
   * back to the browser's zone, which is what it did unconditionally before
   * YAR-403: a co-admin one hour east of the household saw every extracted
   * time an hour late, and an evening event slipped onto the next day.
   */
  timezone?: string | null;
  proposals?: ReviewCardProposals;
  /**
   * Save a new end date on a recurring event's rule (YAR-443). Omit for a
   * read-only card: the recurrence summary still shows, without the control.
   */
  onChangeRecurrenceUntil?: (
    item: ReviewItemWithChild,
    until: string | null,
  ) => Promise<void>;
}) {
  const { event, relatedTasks } = group;
  const taskCount = relatedTasks.length;
  const canEdit = !!onEditItem;
  const proposal = proposals?.resolve(event) ?? { kind: 'none' as const };

  return (
    <div className="flex flex-col items-start">
      {/* Event Card */}
      <div className="w-full rounded-xl border border-border overflow-hidden">
        {/* Event content */}
        <div
          className={['flex gap-3 p-5', canEdit ? 'cursor-pointer' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={canEdit ? () => onEditItem!(event) : undefined}
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onKeyDown={
            canEdit
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') onEditItem!(event);
                }
              : undefined
          }
        >
          {/* Icon with AI badge */}
          <div className="relative flex-shrink-0">
            <div className="flex items-center justify-center w-11 h-11 rounded-md bg-info-bg border border-info-border">
              {/* Plain `calendar`, NOT `calendar-days`: Figma draws this badge
                  as an empty calendar frame, and the day dots lucide's
                  calendar-days adds were what QA flagged (YAR-131, third
                  pass). Deliberately NOT changed on the shared
                  `TaskEventDateIcon` — that renders the small inline date row
                  here and in 8 other surfaces, none of which QA compared. */}
              <Calendar
                size={24}
                // 1.5 is the design-system stroke; lucide defaults to 2,
                // which read heavier than Figma — YAR-131.
                strokeWidth={1.5}
                className="text-info-text"
              />
            </div>
            <div className="absolute -top-2 -left-2 flex items-center justify-center w-5 h-5">
              <Sparkles
                size={20}
                strokeWidth={1.5}
                // Outline in icon/default (#5e5e5e), not a solid #2f2f2f fill —
                // YAR-131. Figma draws the AI mark as a light gray outline.
                className="text-icon-default"
              />
            </div>
          </div>

          {/* Content */}
          {proposal.kind !== 'none' ? (
            <div className="flex-1 min-w-0 flex flex-col gap-3">
              <ProposalSummary
                item={event}
                view={proposal}
                childrenData={childrenData}
                timezone={timezone}
                onKeepAsNew={proposals?.onKeepAsNew}
                keepingAsNew={proposals?.keepingAsNewId === event.id}
              />
              <div className="flex justify-end empty:hidden">
                <MemberAvatars
                  item={event}
                  memberIdsByItem={memberIdsByItem}
                  availableMembers={availableMembers}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <span className="text-base font-semibold text-foreground">
                {event.title}
              </span>
              {event.description && (
                <span className="text-sm text-foreground break-words">
                  {event.description}
                </span>
              )}

              {/* Date / Time / Location */}
              {/* Date and time wrap as whole units: when both don't fit the
                card's narrow column the time moves to the next line intact,
                instead of breaking "3:15–4:00 PM" before "PM". The task card
                below does the same. */}
              <div className="flex flex-col gap-3">
                {event.start_datetime ? (
                  <div className="flex flex-wrap gap-x-3 gap-y-2 items-start">
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                      <TaskEventDateIcon
                        size={20}
                        className="flex-shrink-0"
                      />
                      {/* Range-aware: an event whose end lands on a later day
                        must say so here, or the card asserts a one-day event
                        the email never described (YAR-391). Same-day events —
                        the overwhelming majority, including every one that
                        picked up the pipeline's +15-minute default end — are
                        formatted exactly as before. */}
                      {formatDateRange(
                        event.start_datetime,
                        event.end_datetime,
                        timezone,
                      )}
                    </span>
                    {/* Mirrors the guard the sibling TaskCard already carries:
                      a date-only extraction is stored as local noon purely as
                      a day-boundary guard, so printing it invents an hour the
                      sender never wrote (YAR-376 / YAR-378). Without this a
                      date-only multi-day span — "East Coast College Tour,
                      Mar 29 – Apr 3" — reads as "12:00 PM - 12:00 PM". */}
                    {!event.is_time_unknown && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                        <Clock
                          size={20}
                          className="flex-shrink-0"
                        />
                        {formatTimeRange(
                          event.start_datetime,
                          event.end_datetime,
                          timezone,
                        )}
                      </span>
                    )}
                    {/* Phase 3b: a time read from a vague phrase the family
                      taught ("after school"), while it is unchanged. */}
                    {!event.is_time_unknown &&
                      isMemoryFilled(
                        event.raw_extraction,
                        'time',
                        toTimeKeyTz(event.start_datetime, timezone),
                      ) && (
                        <span className="self-center text-xs text-muted-foreground">
                          {MEMORY_FILL_CAPTION}
                        </span>
                      )}
                  </div>
                ) : (
                  // Amber chip surfaces events the LLM extracted without a
                  // date. An undated event can't be confirmed — Path C blocks
                  // the CTA — so tapping the card opens the edit modal to set
                  // one. Mirrors the TaskCard "Add date" affordance below.
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-warning-light text-warning border border-warning-border text-xs font-medium w-fit">
                    <TaskEventDateIcon
                      size={12}
                      className="flex-shrink-0"
                    />
                    Add date
                  </span>
                )}
                {event.location && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin
                      size={20}
                      className="flex-shrink-0"
                    />
                    {event.location}
                  </span>
                )}
                {/* Phase 3b: a place Yaruta filled from the family's past
                  edits, while it still holds that value. */}
                {isMemoryFilled(
                  event.raw_extraction,
                  'location',
                  event.location,
                ) && (
                  <span className="-mt-2 pl-[26px] text-xs text-muted-foreground">
                    {MEMORY_FILL_CAPTION}
                  </span>
                )}

                {/* Recurring events (YAR-443). Renders nothing unless the
                  item carries a usable rule and a start date. */}
                <RecurrenceSummary
                  item={event}
                  timezone={timezone}
                  onChangeUntil={readOnly ? undefined : onChangeRecurrenceUntil}
                />

                {/* Footer row: child badges on the left, household-member
                  avatars on the right. The two come from different
                  sources — children are AI-extracted from the email,
                  members are user-picked via the Item form. */}
                <div className="flex items-end justify-between gap-3">
                  <ChildBadges
                    item={event}
                    childrenData={childrenData}
                    disambiguation={disambiguation}
                  />
                  <MemberAvatars
                    item={event}
                    memberIdsByItem={memberIdsByItem}
                    availableMembers={availableMembers}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Related Tasks toggle bar */}
        {taskCount > 0 && (
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-between px-5 py-2 bg-surface-secondary border-t border-border cursor-pointer"
          >
            <div className="flex items-center gap-1.5">
              <SquareCheckBig
                size={20}
                className="text-muted-foreground"
              />
              <span className="text-sm font-bold text-muted-foreground">
                {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp
                size={20}
                className="text-muted-foreground"
              />
            ) : (
              <ChevronDown
                size={20}
                className="text-muted-foreground"
              />
            )}
          </button>
        )}
      </div>

      {/* Related Tasks (nested under event with connecting line) */}
      {isExpanded && taskCount > 0 && (
        <div className="flex items-stretch w-full">
          {/* Vertical connecting line */}
          <div className="w-10 flex justify-center flex-shrink-0">
            <div className="w-px bg-border" />
          </div>

          {/* Task cards */}
          {/* min-w-0: this column sits on the main axis of the flex row
              above, so without it the default min-width:auto lets a long
              unbreakable URL in a task description (break-words does NOT
              shrink min-content) blow the column past the card width —
              horizontal overflow on the whole Review Items modal. */}
          <div className="flex-1 min-w-0 flex flex-col gap-3 pt-3">
            {relatedTasks.map((task) => (
              <TaskCard
                key={task.id}
                item={task}
                parentEvent={group.event}
                onEditItem={onEditItem}
                childrenData={childrenData}
                disambiguation={disambiguation}
                memberIdsByItem={memberIdsByItem}
                availableMembers={availableMembers}
                timezone={timezone}
                proposals={proposals}
              />
            ))}

            {/* Add task to event placeholder — hidden once approved or
                when the caller didn't wire up an add handler (e.g. for
                view-only VIEWER members). */}
            {!readOnly && onAddTaskToEvent && (
              <button
                type="button"
                className="w-full rounded-xl border border-dashed border-border-strong py-2.5 flex items-center justify-center gap-1.5 bg-transparent cursor-pointer"
                aria-label="Add task to event"
                onClick={() => onAddTaskToEvent(event)}
              >
                <span className="text-sm font-semibold text-muted-foreground">
                  Add task to event
                </span>
                <Plus
                  size={16}
                  className="text-muted-foreground"
                />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Task Card
// =============================================================================

export function TaskCard({
  item,
  parentEvent,
  onEditItem,
  childrenData = [],
  disambiguation,
  relatedEventTitle,
  memberIdsByItem,
  availableMembers,
  timezone,
  proposals,
  onDontAdd,
  dontAddDisabled = false,
  cardRef,
}: {
  item: ReviewItemWithChild;
  parentEvent?: ReviewItemWithChild;
  onEditItem?: (item: ReviewItemWithChild) => void;
  childrenData?: Child[];
  disambiguation?: DisambiguationMap;
  relatedEventTitle?: string;
  memberIdsByItem?: ItemMemberAssignments;
  availableMembers?: AvailableMember[];
  /** See {@link EventGroupCard}'s `timezone` — same contract, same reason. */
  timezone?: string | null;
  proposals?: ReviewCardProposals;
  /** Review v0 only (YAR-552 E2): the task's own `Don't add`. Absent in
   *  today's Review, which then renders exactly as before. */
  onDontAdd?: (item: ReviewItemWithChild) => void;
  /** Confirm all in flight: aria-disabled, focus kept, tap ignored. */
  dontAddDisabled?: boolean;
  /** Focus target for the Review v0 page (Undo, the next task after a Don't add). */
  cardRef?: Ref<HTMLDivElement>;
}) {
  const proposal = proposals?.resolve(item) ?? { kind: 'none' as const };
  const titleText = (
    <span className="text-base font-semibold text-foreground">
      {item.title}
    </span>
  );
  return (
    <div
      ref={cardRef}
      className="rounded-xl border border-border p-5 flex gap-3 cursor-pointer"
      onClick={() => onEditItem?.(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onEditItem?.(item);
      }}
    >
      {/* Icon with AI badge */}
      <div className="relative flex-shrink-0">
        <div className="flex items-center justify-center w-11 h-11 rounded-md border border-border bg-background">
          <SquareCheckBig
            size={24}
            // Same 1.5 design-system stroke as the event card's icon above —
            // lucide defaults to 2, which QA read as a visibly thicker border
            // on the task icon than on its sibling (YAR-131, third pass).
            strokeWidth={1.5}
            className="text-muted-foreground"
          />
        </div>
        <div className="absolute -top-2 -left-2 flex items-center justify-center w-5 h-5">
          <Sparkles
            size={20}
            strokeWidth={1.5}
            // Outline in icon/default (#5e5e5e), not a solid #2f2f2f fill —
            // YAR-131. Figma draws the AI mark as a light gray outline.
            className="text-icon-default"
          />
        </div>
      </div>

      {/* Content */}
      {proposal.kind !== 'none' ? (
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          <ProposalSummary
            item={item}
            view={proposal}
            childrenData={childrenData}
            timezone={timezone}
            onKeepAsNew={proposals?.onKeepAsNew}
            keepingAsNew={proposals?.keepingAsNewId === item.id}
          />
          <div className="flex justify-end empty:hidden">
            <MemberAvatars
              item={item}
              memberIdsByItem={memberIdsByItem}
              availableMembers={availableMembers}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {onDontAdd ? (
            <div className="flex items-start gap-2">
              {titleText}
              <button
                type="button"
                // The card root is a role="button" that opens the item on
                // click and on Enter/Space: neither may reach it from here.
                // aria-disabled, not native `disabled`, so focus is kept.
                onClick={(e) => {
                  e.stopPropagation();
                  if (!dontAddDisabled) onDontAdd(item);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                aria-label={REVIEW_COPY.actionDontAddNamed(item.title)}
                aria-disabled={dontAddDisabled || undefined}
                // Same as ReviewItemCard's action: 24px tap target, 20px row.
                className="-my-0.5 ml-auto inline-flex min-h-6 shrink-0 cursor-pointer items-center rounded-md px-1 text-sm font-medium text-muted-foreground hover:text-foreground aria-disabled:cursor-not-allowed aria-disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {REVIEW_COPY.actionDontAdd}
              </button>
            </div>
          ) : (
            titleText
          )}
          {item.description && (
            <span className="text-sm text-foreground break-words">
              {item.description}
            </span>
          )}

          <div className="flex flex-col gap-3">
            {item.due_datetime ? (
              <div className="flex flex-wrap gap-x-3 gap-y-2 items-start">
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                  <TaskEventDateIcon
                    size={20}
                    className="flex-shrink-0"
                  />
                  {formatShortDate(item.due_datetime, timezone)}
                </span>
                {/* Mirrors the event card's date+time row, but ONLY when the
                  email actually stated an hour. `is_time_unknown` is the
                  pipeline's flag for that (see _shared/extracted-datetime.ts):
                  a date-only extraction is stored as local noon purely as a
                  day-boundary guard, and rendering that noon would invent a
                  time the sender never wrote (YAR-376 / YAR-378). */}
                {!item.is_time_unknown && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                    <Clock
                      size={20}
                      className="flex-shrink-0"
                    />
                    {formatTime(item.due_datetime, timezone)}
                  </span>
                )}
                {!item.is_time_unknown &&
                  isMemoryFilled(
                    item.raw_extraction,
                    'time',
                    toTimeKeyTz(item.due_datetime, timezone),
                  ) && (
                    <span className="self-center text-xs text-muted-foreground">
                      {MEMORY_FILL_CAPTION}
                    </span>
                  )}
              </div>
            ) : (
              // Yellow chip surfaces tasks that came out of LLM extraction
              // without a due date. Without this they're indistinguishable
              // in /review from fully-dated items until the user expands the
              // card — and silently get blocked from Today/Schedule on
              // approve. Click target stays the parent card (opens the edit
              // modal where the user can set the date or dismiss).
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-warning-light text-warning border border-warning-border text-xs font-medium w-fit">
                <TaskEventDateIcon
                  size={12}
                  className="flex-shrink-0"
                />
                Add date
              </span>
            )}

            {/* Footer row: child badges + event-link badge on the left,
              household-member avatars on the right. The TaskCard
              previously had no right cluster — adding it here so
              standalone tasks show their assignees too. */}
            <div className="flex items-end justify-between gap-3">
              <div className="flex flex-wrap gap-1 items-start flex-1 min-w-0">
                <ChildBadges
                  item={item}
                  childrenData={childrenData}
                  disambiguation={disambiguation}
                />

                {parentEvent && <EventLinkBadge title={parentEvent.title} />}

                {!parentEvent && item.related_event_id && relatedEventTitle && (
                  <EventLinkBadge title={relatedEventTitle} />
                )}
              </div>
              <MemberAvatars
                item={item}
                memberIdsByItem={memberIdsByItem}
                availableMembers={availableMembers}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Child badges (left) + Member avatars (right) — split so cards can compose
// =============================================================================

/**
 * Resolves the AI's child names on a review_item to actual family
 * children (honoring the user's disambiguation picks) and renders one
 * ProfileBadge per match. Unmatched names are intentionally
 * suppressed: a placeholder badge with no avatar reads as "this isn't
 * a real child" — the parent modal's suggestion banner is the proper
 * place to surface them.
 *
 * Returns null when nothing resolves so callers don't have to guard
 * around an empty wrapper.
 */
function ChildBadges({
  item,
  childrenData,
  disambiguation,
}: {
  item: ReviewItemWithChild;
  childrenData: Child[];
  disambiguation?: DisambiguationMap;
}) {
  const names = namesFor(item);
  if (names.length === 0) return null;

  const seen = new Set<string>();
  const profiles: Array<{
    name: string;
    emoji: string | undefined;
    color: string | undefined;
    imagePath: string | undefined;
  }> = [];
  for (const name of names) {
    const child = resolveAiChildName(name, childrenData, disambiguation);
    if (!child) continue;
    if (seen.has(child.id)) continue;
    seen.add(child.id);
    profiles.push({
      name: child.name,
      emoji: child.emoji_avatar ?? undefined,
      color: child.emoji_color ?? undefined,
      imagePath: child.avatar_image_path ?? undefined,
    });
  }
  if (profiles.length === 0) return null;

  const badges = (
    <div className="flex flex-wrap gap-1 items-start">
      {profiles.map((p) => (
        <ProfileBadge
          key={p.name}
          name={p.name}
          emoji={p.emoji}
          color={p.color}
          imagePath={p.imagePath}
        />
      ))}
    </div>
  );
  // A child Yaruta matched from the email's own words ("Summer B."), while
  // the item still carries exactly that child.
  const caption = childMentionCaption(item.raw_extraction, names);
  if (!caption) return badges;
  return (
    <div className="flex flex-col gap-1 items-start">
      {badges}
      <span className="text-xs text-muted-foreground">{caption}</span>
    </div>
  );
}

/**
 * Right-side cluster of household-member avatars for a review item.
 *
 * Members are *assigned* via the ItemFormModal Members dropdown — they
 * are NOT derived from the AI extraction (children live in
 * ChildBadges). The map is keyed by review_item.id; user_ids are
 * resolved against `availableMembers` for emoji/color/image, so an id
 * that no longer belongs to the household is silently dropped (the
 * edge function also filters at commit time as defense in depth).
 *
 * Returns null when nothing is assigned so the card footer can stay
 * compact for the legacy "no members picked" case.
 */
function MemberAvatars({
  item,
  memberIdsByItem,
  availableMembers,
}: {
  item: ReviewItemWithChild;
  memberIdsByItem?: ItemMemberAssignments;
  availableMembers?: AvailableMember[];
}) {
  if (!memberIdsByItem || !availableMembers || availableMembers.length === 0) {
    return null;
  }
  const ids = memberIdsByItem.get(item.id);
  if (!ids || ids.length === 0) return null;
  const byId = new Map(availableMembers.map((m) => [m.user_id, m]));
  const seen = new Set<string>();
  const avatars: Array<{
    emoji: string | undefined;
    color: string | undefined;
    alt: string;
    imagePath: string | undefined;
  }> = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    const m = byId.get(id);
    if (!m) continue;
    seen.add(id);
    avatars.push({
      emoji: m.avatar_emoji ?? undefined,
      color: m.avatar_color ?? undefined,
      alt: m.display_name,
      imagePath: m.avatar_image_path ?? undefined,
    });
  }
  if (avatars.length === 0) return null;

  return (
    <AvatarGroup
      avatars={avatars}
      max={3}
      size="sm"
    />
  );
}

function namesFor(item: ReviewItemWithChild): string[] {
  if (item.child_names.length > 0) return item.child_names;
  return item.child_name ? [item.child_name] : [];
}

// =============================================================================
// Child Badge
// =============================================================================

export function ChildBadge({
  name,
  emoji,
}: {
  name: string;
  emoji?: string | null;
}) {
  return (
    <span className="inline-flex items-center gap-1 bg-background border border-border rounded-full px-2 py-0.5 text-xs text-foreground">
      {emoji && <span className="text-[10px]">{emoji}</span>}
      {name}
    </span>
  );
}

// =============================================================================
// Source Reference Section
// =============================================================================

// The expanded panel and its helper live in `EmailSourcePanel` (the details
// sheet reuses them); re-exported so this module stays the single import.
export {
  getAttachments,
  type AttachmentInfo,
} from '@/components/domain/shared/EmailSourcePanel';

export function SourceReferenceSection({
  email,
  attachments,
  attachmentCount,
  expanded,
  onToggle,
}: {
  email: Email;
  attachments: AttachmentInfo[];
  attachmentCount: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  // A photo or file the parent added (YAR-556; Figma View Source Reference
  // Kind=photo): its own icon and line, and the photo in place of a body.
  const upload = uploadDisplayKind(email);
  const KindIcon = upload ? (upload === 'file' ? FileText : ImageIcon) : Mail;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 bg-transparent border-none cursor-pointer p-5 text-left"
      >
        <KindIcon
          size={24}
          className="text-muted-foreground flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-bold text-foreground block">
            View Source Reference
          </span>
          <span className="text-xs text-foreground">
            {upload
              ? ADD_TO_YARUTA_COPY.sourceReferenceSubline(upload)
              : 'Original message'}
            {!upload &&
              attachmentCount > 0 &&
              ` & ${attachmentCount} attachment${attachmentCount !== 1 ? 's' : ''}`}
          </span>
        </div>
        {expanded ? (
          <ChevronUp
            size={24}
            className="text-muted-foreground flex-shrink-0"
          />
        ) : (
          <ChevronDown
            size={24}
            className="text-muted-foreground flex-shrink-0"
          />
        )}
      </button>

      {expanded &&
        (upload ? (
          <UploadSourcePanel
            emailId={email.id}
            kind={upload}
          />
        ) : (
          <EmailSourcePanel
            email={email}
            attachments={attachments}
          />
        ))}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Extract distinct http(s) URLs from the email's processed body. Used by
 * the Review inbox card to surface a "N links" dropdown when the email
 * has more than one. The regex is intentionally loose — we just want a
 * URL list for the user to pick from, so trailing punctuation is
 * trimmed and duplicates are collapsed in insertion order.
 */
export function getEmailLinks(email: Email): string[] {
  if (!email.processed_content) return [];
  const raw = email.processed_content.match(/https?:\/\/[^\s<>()"']+/g) ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (let url of raw) {
    // Trim common trailing punctuation (., ,, ), ], ;, :)
    url = url.replace(/[.,)\]};:]+$/, '');
    if (!seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}
